import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {ConfigService} from '@config/config.service';
import {Actions, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {CityDto} from '@services/associations/model/cityDto';
import {PageStreetDto} from '@services/associations/model/pageStreetDto';
import {StreetDto} from '@services/associations/model/streetDto';
import {ListDialogComponent} from '@shared/components/list-dialog/list-dialog.component';
import {ListItemType} from '@shared/enum/list-item-type';
import {DictionariesService} from '@shared/services/dictionaries.service';
import {SharedService} from '@shared/shared.service';
import {ValidateIfObject} from '@shared/validators/if-object.validator';
import {BehaviorSubject, catchError, debounceTime, EMPTY, Subject, takeUntil} from 'rxjs';
import {map, take} from 'rxjs/operators';
import * as associationActions from '@clients/ngrx/actions/associations.actions';
import {AssociationListResponse} from "@services/back-office/model/associationListResponse";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {OrganizationRequest} from '@services/back-office/model/organizationRequest';
import {KatottgService} from '@shared/services/katottg.service';
import {KatottgModel} from '@services/back-office/model/katottgModel';
import {PageKatottgModel} from '@services/back-office/model/pageKatottgModel';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-association-dialog',
    templateUrl: './association-dialog.component.html',
    styleUrls: ['./association-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AssociationDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    associationForm: UntypedFormGroup;
    /** Address */
    katottgList$ = new BehaviorSubject<KatottgModel[] | null | undefined>(null);
    streets$ = new BehaviorSubject<StreetDto[] | null | undefined>(null);
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            edit?: boolean,
            association?: AssociationListResponse,
        },
        public dialogRef: MatDialogRef<AssociationDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private dictionariesService: DictionariesService,
        private katottgService: KatottgService,
        private sharedService: SharedService,
        private config: ConfigService,
        private store$: Store<Reducers>,
        private actions$: Actions,
        private toastr: ToastrService,
        private translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        if (!this.associationForm) {
            this.initForm();
        }
        /** Patch Form on Edit */
        if (this.data.edit && this.data.association) {
            this.patchForm(this.data.association);
        }
        this.watchFormChanges();
        /** Watch Add or Edit Association Action */
        this.watchAddOrEditAssociationSuccess();
        this.watchAddOrEditAssociationFailure();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.katottgList$.complete();
        this.streets$.complete();
        this.loading$.complete();
    }

    initForm() {
        this.associationForm = this.formBuilder.group({
            type: ['HOUSING_COOP', [Validators.required]],
            companyType: ['', [Validators.required]],
            code: ['', [Validators.required]],
            name: ['', [Validators.required]],
            city: ['', [Validators.required, ValidateIfObject]],
            street: [''],
            number: [''],
            financeEnabled: [true],
            showActs: [false],
            apartmentStreetAbsent: [false],
            hideAssociationGroup: [false],
            meeting: [true],
        });

        this.updateConditionalValidators(this.associationForm.get('type')?.value);

        this.associationForm.get('type')?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((type) => {
            this.updateConditionalValidators(type);
        });
    }

    updateConditionalValidators(type: string) {
        const streetControl = this.associationForm.get('street');
        const numberControl = this.associationForm.get('number');

        if (type !== 'PRIVATE_SECTOR_COOP') {
            streetControl?.setValidators([Validators.required, ValidateIfObject]);
            numberControl?.setValidators([Validators.required]);
        } else {
            streetControl?.clearValidators();
            numberControl?.clearValidators();
        }

        streetControl?.updateValueAndValidity();
        numberControl?.updateValueAndValidity();
    }

    /** Patch Form on Edit */
    patchForm(association: AssociationListResponse) {
        if (!this.associationForm) {
            this.initForm();
        }

        this.associationForm.patchValue({
            ...association.type && {type: association.type},
            ...association.companyType && {companyType: association.companyType},
            ...association.code && {code: association.code},
            ...association.name && {name: association.name},
            ...association.city && {city: {id: association.city.id, name: association.city.name}},
            ...association.street && {street: {id: association.street.id, name: association.street.name}},
            ...association.number && {number: association.number},
            financeEnabled: !!association.financeEnabled,
            showActs: !!association.showActs,
            hideAssociationGroup: !!association.hideAssociationGroup,
            apartmentStreetAbsent: !!association.apartmentStreetAbsent,
            meeting: !!association.meeting,
        });
    }

    watchFormChanges() {
        if (!this.associationForm) {
            this.initForm();
        }

        this.associationForm.controls['city'].valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(this.config.searchTimeout),
        ).subscribe(value => {
            if (typeof value === 'string' && value.trim().length > 2) {
                this.getCities(value);
            }

            if (typeof value === 'string' && value.length === 0) {
                this.katottgList$.next([]);
            }
        });

        this.associationForm.controls['street'].valueChanges.pipe(
            takeUntil(this.destroyed$),
            debounceTime(this.config.searchTimeout),
        ).subscribe(value => {
            if (typeof value === 'string' && value.trim().length > 2) {
                this.getStreets(value);
            }

            if (typeof value === 'string' && value.length === 0) {
                this.streets$.next([]);
            }
        });
    }

    getCities(name: string) {
        this.katottgService.getKatottg({
            query: name,
            categories: new Set(['CITY', 'SETTLEMENT', 'VILLAGE'])
        }).pipe(
            takeUntil(this.destroyed$),
            map((response: PageKatottgModel) => {
                this.katottgList$.next(response.content);
            }),
            catchError(() => {
                return EMPTY;
            })
        ).subscribe();
    }

    displayCityFn(city: CityDto): string {
        return city ? city.name! : '';
    }

    cityIsSelected() {
        this.katottgList$.next(null);
    }

    streetInputChanges(event: string | StreetDto) {
        if (typeof event === 'string') {
            this.getStreets(event);
        }
    }

    getStreets(streetName: string) {
        const cityId = this.associationForm.get('city')?.value.id;
        if (!cityId) {
            return;
        }
        this.dictionariesService.getStreets({name: streetName, activeOnly: true, cityId: cityId}).pipe(
            takeUntil(this.destroyed$),
            map((response: PageStreetDto) => {
                this.streets$.next(response.content);
            }),
            catchError(() => {
                return EMPTY;
            })
        ).subscribe();
    }

    displayStreetFn(street: StreetDto): string {
        return street ? street.name! : '';
    }

    streetIsSelected() {
        this.streets$.next(null);
    }

    openListDialog() {
        const cityId = this.associationForm.get('city')?.value.id;
        if (!cityId) {
            return;
        }

        const listDialog$ = this.sharedService.openDialog(
            ListDialogComponent,
            {...this.config.itemsListDialog, hasBackdrop: true},
            {
                itemsType: ListItemType.STREET,
                props: {
                    cityId: cityId
                }
            }
        );

        listDialog$.afterClosed().pipe(
            take(1),
        ).subscribe(dialogData => {
            if (dialogData.result && dialogData.data) {
                this.associationForm.get('street')?.setValue({
                    id: dialogData.data.id,
                    name: dialogData.data.name
                });
            }
        });
    }

    public normalizeName(value: string): string {
        return value
            ?.normalize('NFC')
            .replace(/[\p{Cf}\p{Cc}]/gu, '')
            .replace(/\s+/g, ' ')
            .trim() ?? '';
    }

    /** Add or Edit Association */
    submitForm() {
        this.loading$.next(true);
        const formData = this.associationForm.getRawValue();

        if (!this.isValidCode(formData.code, formData.companyType)) {
            this.toastr.error(this.translateService.instant('responseErrors.association_company_type_invalid.text'));
            this.loading$.next(false);
            return;
        }

        const request: OrganizationRequest = {
            ...(formData.type === 'PRIVATE_SECTOR_COOP' || formData.type === 'GARAGE_COOP') && {apartmentStreetAbsent: formData.apartmentStreetAbsent},
            ...formData.companyType && {companyType: formData.companyType},
            ...formData.code && {code: formData.code},
            financeEnabled: formData.financeEnabled,
            hideAssociationGroup: formData.hideAssociationGroup,
            meeting: formData.meeting,
            showActs: formData.showActs,
            ...formData.name && {name: this.normalizeName(formData.name)},
            ...formData.number && {number: formData.number},
            ...formData.street && {streetId: formData.street.id},
            type: formData.type,
        };

        if (this.data.edit && this.data.association) {
            this.store$.dispatch(new associationActions.EditAssociations({associationId: this.data.association.id!, request}));
        } else {
            this.store$.dispatch(new associationActions.AddAssociations(request));
        }
    }

    isValidCode(code: string, companyType: 'LEGAL' | 'INDIVIDUAL'): boolean {
        if (!code || !companyType) {
            return false;
        }

        const length = code.toString().length;
        if (length < 8 || length > 10) {
            return false;
        }

        if (length === 8 && companyType !== 'LEGAL') {
            return false;
        }

        return !(length === 10 && companyType !== 'INDIVIDUAL');
    }

    /** Watch Add or Edit Association Action */
    watchAddOrEditAssociationSuccess() {
        this.actions$.pipe(
            ofType(
                associationActions.AssociationsActionTypes.AddAssociationsSuccess,
            ),
            take(1),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe((action: associationActions.AddAssociationsSuccess) => {
            this.loading$.next(false);
            if (action.payload.id) {
                this.sharedService.openCrmItem('ASSOCIATION' as ListItemType, action.payload.id);
            }
            this.closeDialog(true);
        });

        this.actions$.pipe(
            ofType(
                associationActions.AssociationsActionTypes.EditAssociationsSuccess,
            ),
            take(1),
            catchError(() => {
                this.loading$.next(false);
                return EMPTY;
            })
        ).subscribe(() => {
            this.loading$.next(false);
            this.closeDialog(true);
        });
    }

    watchAddOrEditAssociationFailure() {
        this.actions$.pipe(
            ofType(
                associationActions.AssociationsActionTypes.AddAssociationsFailure,
                associationActions.AssociationsActionTypes.EditAssociationsFailure,
            ),
            take(1)
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }

}
